/**
 * FaviconBadge — dibuja el favicon (Z sobre terracota) en un canvas y le añade
 * un "globo" rojo con el nº de avisos sin leer cuando los hay. También refleja
 * el contador en el título de la pestaña. Comparte la query de notificaciones
 * con la campana (mismo queryKey → sin peticiones duplicadas).
 */
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const BASE_TITLE = "Zicasso · Leads";

function drawFavicon(count) {
  const size = 64;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  // fondo redondeado con gradiente terracota
  const grad = ctx.createLinearGradient(0, 0, size, size);
  grad.addColorStop(0, "#E37E5E");
  grad.addColorStop(1, "#C45A45");
  const r = 14;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(size - r, 0);
  ctx.arcTo(size, 0, size, r, r);
  ctx.lineTo(size, size - r);
  ctx.arcTo(size, size, size - r, size, r);
  ctx.lineTo(r, size);
  ctx.arcTo(0, size, 0, size - r, r);
  ctx.lineTo(0, r);
  ctx.arcTo(0, 0, r, 0, r);
  ctx.closePath();
  ctx.fillStyle = grad;
  ctx.fill();

  // "Z" (mismo trazo que el favicon estático, escalado)
  ctx.strokeStyle = "#FFFFFF";
  ctx.lineWidth = 6.8;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(18, 18);
  ctx.lineTo(46, 18);
  ctx.lineTo(18, 46);
  ctx.lineTo(46, 46);
  ctx.stroke();

  // globo de avisos
  if (count > 0) {
    const bx = size - 15;
    const by = 15;
    const br = 15;
    ctx.beginPath();
    ctx.arc(bx, by, br, 0, Math.PI * 2);
    ctx.fillStyle = "#FFFFFF"; // aro blanco para contraste
    ctx.fill();
    ctx.beginPath();
    ctx.arc(bx, by, br - 3, 0, Math.PI * 2);
    ctx.fillStyle = "#E11D48"; // rojo aviso
    ctx.fill();
    ctx.fillStyle = "#FFFFFF";
    ctx.font = "bold 18px -apple-system, 'Segoe UI', Roboto, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(count > 9 ? "9+" : String(count), bx, by + 1);
  }
  return canvas.toDataURL("image/png");
}

function setFavicon(href) {
  let link = document.querySelector("link[data-dynamic-favicon]");
  if (!link) {
    document.querySelectorAll('link[rel~="icon"]').forEach((l) => l.parentNode && l.parentNode.removeChild(l));
    link = document.createElement("link");
    link.rel = "icon";
    link.type = "image/png";
    link.setAttribute("data-dynamic-favicon", "true");
    document.head.appendChild(link);
  }
  link.href = href;
}

export default function FaviconBadge() {
  const notifQ = useQuery({
    queryKey: ["zc-notifications"],
    queryFn: async () => (await api.get("/zicasso/notifications")).data,
    refetchInterval: 60_000,
  });
  const tasksQ = useQuery({
    queryKey: ["tasks-summary"],
    queryFn: async () => (await api.get("/zicasso/tasks/summary")).data,
    refetchInterval: 60_000,
  });
  const count = (notifQ.data?.unread_count || 0) + (tasksQ.data?.pending || 0);

  useEffect(() => {
    try {
      setFavicon(drawFavicon(count));
    } catch {
      /* canvas no disponible: se mantiene el favicon estático */
    }
    document.title = count > 0 ? `(${count > 99 ? "99+" : count}) ${BASE_TITLE}` : BASE_TITLE;
  }, [count]);

  return null;
}
